/**
 * The saved-view rail — Step 42.
 *
 * Kai: *"just having a ticket list on the side bar, then a secondary side bar
 * with all the custom views etc"*, matching the reference, whose view rail
 * sits inside the tickets page rather than in the app nav.
 *
 * ── Why the views left the main sidebar ────────────────────────────────
 *
 * Scale, not taste. That list was EVERY view, so at Kai's thirty-five it
 * would have pushed Catalogue, Articles, Reports and Settings below the fold
 * — and a nav whose primary destinations are off-screen is not a nav. Views
 * now sit beside the list they filter, which is also where somebody already
 * is when they want to switch.
 *
 * ── Starred, because thirty-five is a directory ────────────────────────
 *
 * The reference says so in its own help text: *"Press the arrow keys to
 * navigate the views. Press the tab key to star & un-star a view."* With a
 * handful of views a flat list is fine; with thirty-five it is a filing
 * cabinet, and the short list is what people actually work from.
 *
 * Arrow-key navigation is implemented for the same reason it exists there:
 * this rail is used dozens of times a day by people who never touch the
 * mouse for it.
 */
import { useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Bookmark, Copy, MoreHorizontal, PanelLeftClose, Pencil, Plus, Star, Trash2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { apiSend } from '../../lib/api';
import { qk } from '../../lib/queryClient';
import { cn } from '../../lib/utils';
import type { SavedView } from '../../features/tickets/views';
import { useConfirm } from '../../lib/confirm';

const starred = (v: SavedView) => v.isStarred === 1 || v.isStarred === true;

export function ViewRail({
  views,
  builtinIds,
  activeId,
  onPick,
  onNew,
  onRename,
  onClose,
  onOpenBin,
  binActive,
}: {
  views: SavedView[];
  /** Built-ins cannot be starred, renamed or deleted — they are code, not
   *  rows, and offering the action would be a button that fails. */
  builtinIds: Set<string>;
  activeId: string | null;
  onPick: (v: SavedView) => void;
  onNew: () => void;
  onRename: (v: SavedView) => void;
  onClose: () => void;
  /** Step 111 — open the bin. Not a saved view, so not in the list. */
  onOpenBin: () => void;
  binActive: boolean;
}) {
  const confirm = useConfirm();
  const qc = useQueryClient();
  const listRef = useRef<HTMLDivElement>(null);

  const invalidate = () => void qc.invalidateQueries({ queryKey: qk.views });
  const patch = useMutation({
    mutationFn: (v: { id: string; body: Record<string, unknown> }) =>
      apiSend(`/api/views/${v.id}`, 'PATCH', v.body),
    onSuccess: invalidate,
  });
  const duplicate = useMutation({
    mutationFn: (v: SavedView) =>
      apiSend('/api/views', 'POST', {
        name: `${v.name} (copy)`,
        scope: v.scope,
        groupId: v.groupId ?? null,
        query: v.query,
        ...(v.columns ? { columns: v.columns } : {}),
        ...(v.pageSize ? { pageSize: v.pageSize } : {}),
      }),
    onSuccess: invalidate,
  });
  const remove = useMutation({
    mutationFn: (id: string) => apiSend(`/api/views/${id}`, 'DELETE'),
    onSuccess: invalidate,
  });

  const stars = views.filter(starred);
  const rest = views.filter((v) => !starred(v));

  /**
   * Arrow keys move between views; the rail is a listbox, so that is both the
   * correct ARIA and the behaviour the reference documents.
   */
  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    e.preventDefault();
    const items = Array.from(listRef.current?.querySelectorAll<HTMLElement>('[data-view]') ?? []);
    const at = items.findIndex((el) => el === document.activeElement);
    const next = e.key === 'ArrowDown' ? at + 1 : at - 1;
    items[Math.max(0, Math.min(items.length - 1, next))]?.focus();
  };

  const Row = ({ v }: { v: SavedView }) => {
    const isBuiltin = builtinIds.has(v.id);
    const active = activeId === v.id;
    return (
      <div
        className={cn(
          'group flex items-center gap-1 rounded-md pr-1',
          active ? 'bg-accent' : 'hover:bg-accent/60',
        )}
      >
        <button
          data-view
          role="option"
          aria-selected={active}
          onClick={() => {
            onPick(v);
            onClose();
          }}
          className={cn(
            'min-w-0 flex-1 truncate px-2 py-1.5 text-left text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            active ? 'font-medium text-accent-foreground' : 'text-muted-foreground',
          )}
          title={v.name}
        >
          {v.name}
        </button>
        {!isBuiltin && (
          <>
            <button
              aria-label={starred(v) ? `Un-star ${v.name}` : `Star ${v.name}`}
              onClick={() => patch.mutate({ id: v.id, body: { isStarred: !starred(v) } })}
              className={cn(
                'shrink-0 rounded p-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                // A star already given stays visible; the rest appear on hover
                // so the rail is a list of names, not a wall of icons.
                starred(v)
                  ? 'text-amber-500'
                  : 'text-muted-foreground opacity-0 group-hover:opacity-100 hover:text-foreground',
              )}
            >
              <Star className={cn('h-3.5 w-3.5', starred(v) && 'fill-current')} />
            </button>
            <details className="relative shrink-0">
              <summary
                aria-label={`Actions for ${v.name}`}
                className="cursor-pointer list-none rounded p-1 text-muted-foreground opacity-0 hover:text-foreground group-hover:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <MoreHorizontal className="h-3.5 w-3.5" />
              </summary>
              <div className="absolute right-0 z-20 mt-1 w-40 rounded-md border bg-card p-1 shadow-lg">
                <button
                  onClick={() => onRename(v)}
                  className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-xs hover:bg-accent"
                >
                  <Pencil className="h-3.5 w-3.5" /> Edit
                </button>
                <button
                  onClick={() => duplicate.mutate(v)}
                  className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-xs hover:bg-accent"
                >
                  <Copy className="h-3.5 w-3.5" /> Duplicate
                </button>
                <button
                  onClick={async () => {
                    if (
                      await confirm({
                        title: `Delete the view “${v.name}”?`,
                        body: 'The tickets are unaffected.',
                        destructive: true,
                        confirmLabel: 'Delete',
                      })
                    )
                      remove.mutate(v.id);
                  }}
                  className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-xs text-destructive hover:bg-destructive/10"
                >
                  <Trash2 className="h-3.5 w-3.5" /> Delete
                </button>
              </div>
            </details>
          </>
        )}
      </div>
    );
  };

  return (
    /**
     * An OVERLAY, not a third column.
     *
     * Kai: *"the views panel won't collapse (it should open over ticket
     * content, then collapsed)"*. As a permanent column it and the filter
     * panel together took 340px off a 1290px window, and the ticket list was
     * cut off mid-column — visible in his screenshot, where the updated time
     * runs off the right edge.
     *
     * So it floats above the list instead. The list keeps its full width and
     * stays scrollable behind, and closing the rail costs nothing because
     * nothing reflows.
     */
    <aside className="absolute inset-y-0 left-0 z-30 flex w-full flex-col border-r bg-card shadow-xl sm:w-60">
      <div className="flex h-14 shrink-0 items-center gap-2 border-b px-3">
        <Bookmark className="h-4 w-4 text-muted-foreground" />
        <span className="text-sm font-semibold">Views</span>
        <div className="flex-1" />
        <Button size="sm" variant="ghost" onClick={onNew} aria-label="New view">
          <Plus />
        </Button>
        <Button size="sm" variant="ghost" onClick={onClose} aria-label="Close views">
          <PanelLeftClose />
        </Button>
      </div>

      <div
        ref={listRef}
        role="listbox"
        aria-label="Saved views"
        onKeyDown={onKeyDown}
        className="min-h-0 flex-1 overflow-y-auto p-2"
      >
        {stars.length > 0 && (
          <>
            <p className="px-2 pb-1 pt-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              Starred
            </p>
            {stars.map((v) => (
              <Row key={v.id} v={v} />
            ))}
          </>
        )}
        <p className="px-2 pb-1 pt-3 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
          {stars.length > 0 ? 'All views' : 'Views'}
        </p>
        {rest.map((v) => (
          <Row key={v.id} v={v} />
        ))}
        {rest.length === 0 && stars.length === 0 && (
          <p className="px-2 py-3 text-xs text-muted-foreground">No views yet.</p>
        )}
      </div>

      {/* Step 111 — the recycle bin needs a way IN.
          Pinned to the bottom, below the views and separated by a rule: it is
          not a saved view (it cannot be starred, renamed or shared) and
          putting it in the list would invite exactly those actions. Bottom
          because it is where you go occasionally to recover something, not a
          queue you work from. */}
      <div className="shrink-0 border-t p-2">
        <button
          onClick={onOpenBin}
          className={cn(
            'flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            binActive && 'bg-accent font-medium',
          )}
        >
          <Trash2 className="h-4 w-4 shrink-0 text-muted-foreground" />
          Recycle bin
        </button>
      </div>
    </aside>
  );
}
